import { WHATSAPP_URL, WHATSAPP_LABEL, LINKEDIN_URL, LINKEDIN_LABEL, WhatsAppIcon, LinkedInIcon } from "@/components/social-links";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import logo from "@/assets/smart-word-labs-logo.png.asset.json";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useIsAdmin } from "@/hooks/useIsAdmin";

function AdminNavLink({ className }: { className?: string }) {
  const { isAdmin } = useIsAdmin();
  const { t } = useTranslation();
  if (!isAdmin) return null;
  return (
    <Link to="/admin" className={className}>
      {t("nav.admin")}
    </Link>
  );
}


function useSessionUser() {
  const [user, setUser] = useState<{ id: string } | null>(null);
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    import("@/integrations/supabase/client").then(({ supabase }) => {
      if (cancelled) return;
      supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
      const { data } = supabase.auth.onAuthStateChange((_e, session) =>
        setUser(session?.user ?? null),
      );
      unsubscribe = () => data.subscription.unsubscribe();
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);
  return user;
}

export function SiteHeader() {
  const { t } = useTranslation();
  const { t: tc } = useTranslation("chrome");
  const user = useSessionUser();
  return (
    <header className="sticky top-0 z-30 bg-primary text-primary-foreground">
      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 py-4 lg:flex lg:justify-between">
        <Link to="/" className="flex min-w-0 items-center gap-3">
          <img
            src={logo.url}
            alt={tc("logoAlt")}
            width={1920}
            height={1047}
            decoding="async"
            className="h-10 w-auto shrink-0 object-contain transition-transform duration-300 hover:scale-105"
          />
          <span className="sr-only">{tc("logoAlt")}</span>
        </Link>

        <nav className="hidden items-center gap-8 text-sm font-medium text-primary-foreground/85 lg:flex">
          <Link to="/how-it-works" className="transition-colors hover:text-accent">
            {t("nav.howItWorks")}
          </Link>
          <Link to="/programmes" className="transition-colors hover:text-accent">
            {t("nav.programmes")}
          </Link>
          <Link to="/pricing" className="transition-colors hover:text-accent">
            {t("nav.pricing")}
          </Link>
          <Link to="/contact" className="transition-colors hover:text-accent">
            {t("nav.contact")}
          </Link>
          <AdminNavLink className="transition-colors hover:text-accent" />
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Button variant="hero" size="sm" className="shrink-0 rounded-lg" asChild>
              <Link to="/dashboard">{t("nav.portal")}</Link>
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hidden text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground sm:inline-flex"
                asChild
              >
                <Link to="/auth">{t("nav.signIn")}</Link>
              </Button>
              <Button variant="hero" size="sm" className="rounded-lg" asChild>
                <Link to="/book">{t("nav.book")}</Link>
              </Button>
            </>
          )}
        </div>

      </div>
    </header>
  );
}

export function SiteFooter() {
  const { t } = useTranslation();
  const { t: tc } = useTranslation("chrome");
  return (
    <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
      <nav className="mb-3 flex flex-wrap items-center justify-center gap-5">
        <Link to="/programmes" className="transition-colors hover:text-accent">
          {t("nav.programmes")}
        </Link>
        <Link to="/pricing" className="transition-colors hover:text-accent">
          {t("nav.pricing")}
        </Link>
        <Link to="/how-it-works" className="transition-colors hover:text-accent">
          {t("nav.howItWorks")}
        </Link>
        <Link to="/resources" className="transition-colors hover:text-accent">
          {t("nav.resources")}
        </Link>
        <Link to="/reviews" className="transition-colors hover:text-accent">
          {t("nav.reviews")}
        </Link>

        <Link to="/contact" className="transition-colors hover:text-accent">
          {t("nav.contact")}
        </Link>
        <Link to="/auth" className="transition-colors hover:text-accent">
          {t("nav.signIn")}
        </Link>
        <Link to="/book" className="transition-colors hover:text-accent">
          {t("nav.book")}
        </Link>
        <Link to="/terms" className="transition-colors hover:text-accent">
          {t("nav.terms")}
        </Link>
        <Link to="/refund-policy" className="transition-colors hover:text-accent">
          {t("nav.refund")}
        </Link>
        <Link to="/privacy" className="transition-colors hover:text-accent">
          {t("nav.privacy")}
        </Link>

      </nav>
      <div className="mb-3 flex items-center justify-center gap-4">
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label={WHATSAPP_LABEL} className="transition-colors hover:text-accent">
          <WhatsAppIcon className="size-5" />
        </a>
        <a href={LINKEDIN_URL} target="_blank" rel="noopener" aria-label={LINKEDIN_LABEL} className="transition-colors hover:text-accent">
          <LinkedInIcon className="size-5" />
        </a>
      </div>
      {tc("footer.copyright", { year: new Date().getFullYear(), tagline: t("nav.tagline") })}
    </footer>
  );
}
